// Ported from econnect_dm-qwik-frontend/src/components/icons/*.tsx.
// Stock glyphs come from react-icons (path data verified identical to the legacy SVGs);
// bespoke artwork is ported verbatim.
import type { CSSProperties } from "react";
import {
  BsFiletypeCsv,
  BsFiletypeJson,
  BsFiletypePdf,
  BsFiletypeXml,
  BsMailboxFlag,
} from "react-icons/bs";
import { FaBell, FaGithub } from "react-icons/fa6";
import { FiEdit } from "react-icons/fi";
import {
  HiCheckCircle,
  HiHeart,
  HiHome,
  HiMinusSm,
  HiOutlineBookmark,
  HiOutlineChevronRight,
  HiOutlineCreditCard,
  HiOutlineFilter,
  HiOutlineHeart,
  HiOutlineLockClosed,
  HiOutlineShoppingBag,
  HiOutlineStar,
  HiOutlineStop,
  HiOutlineX,
  HiOutlineXCircle,
  HiPlusSm,
  HiUser,
} from "react-icons/hi";
import {
  HiAdjustmentsHorizontal,
  HiEllipsisVertical,
  HiOutlineCalendar,
  HiOutlineCheck,
  HiOutlineClock,
  HiOutlineDocumentText,
  HiOutlineEnvelope,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineHashtag,
  HiOutlineMapPin,
  HiOutlinePencil,
  HiOutlinePencilSquare,
  HiOutlineShieldCheck,
  HiOutlineUserCircle,
  HiOutlineXMark,
} from "react-icons/hi2";
import {
  MdAlternateEmail,
  MdArrowBack,
  MdArrowForward,
  MdCheck,
  MdLogout,
  MdMyLocation,
  MdOutlineCampaign,
  MdOutlineFileCopy,
  MdOutlineHome,
  MdOutlineHomeWork,
  MdOutlinePersonOutline,
  MdOutlineSave,
  MdOutlineShoppingCart,
  MdRestore,
  MdSearch,
  MdSmartphone,
} from "react-icons/md";
import { PiFileImageDuotone } from "react-icons/pi";
import { VscRename } from "react-icons/vsc";

export type IconProps = {
  className?: string;
  width?: number | string;
  height?: number | string;
  forcedClass?: string;
  title?: string;
};

// Legacy Qwik icons took `forcedClass` to *replace* the default class. `className` is the
// React-idiomatic alias with the same override semantics; `forcedClass` wins if both are given.
const cls = (p: IconProps, fallback?: string) => p.forcedClass ?? p.className ?? fallback;

// ponytail: react-icons hard-codes width/height attrs to `size`, so per-axis overrides go
// through inline style (CSS beats presentation attributes). Undefined when nothing was passed
// so legacy CSS sizing rules keep working.
const dims = (p: IconProps): CSSProperties | undefined =>
  p.width === undefined && p.height === undefined
    ? undefined
    : { width: p.width, height: p.height };

// ---------------------------------------------------------------------------
// react-icons wrappers
// ---------------------------------------------------------------------------

export function BookmarkIcon(p: IconProps) {
  return <HiOutlineBookmark className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function BellIcon(p: IconProps) {
  return <FaBell className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function CampaignOutlineIcon(p: IconProps) {
  return <MdOutlineCampaign className={cls(p, "campaign-icon")} title={p.title} style={dims(p)} />;
}

export function CalendarIcon(p: IconProps) {
  return <HiOutlineCalendar className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function CartIcon(p: IconProps) {
  return (
    <MdOutlineShoppingCart className={cls(p, "orders-icon")} title={p.title} style={dims(p)} />
  );
}

export function CheckCircleIcon(p: IconProps) {
  return (
    <HiCheckCircle className={cls(p, "h-5 w-5 text-primary-600")} title={p.title} style={dims(p)} />
  );
}

export function CheckIcon(p: IconProps) {
  return <HiOutlineCheck className={cls(p, "w-6 h-6")} title={p.title} style={dims(p)} />;
}

export function ChevronRightIcon(p: IconProps) {
  return (
    <HiOutlineChevronRight
      className={cls(p, "w-5 h-5 text-gray-300 ml-4")}
      title={p.title}
      style={dims(p)}
    />
  );
}

export function ClockIcon(p: IconProps) {
  return <HiOutlineClock className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function CloseIcon(p: IconProps) {
  return <HiOutlineX className={cls(p, "h-6 w-6")} title={p.title} style={dims(p)} />;
}

export function CreditCardIcon(p: IconProps) {
  return <HiOutlineCreditCard className={cls(p, "w-5 h-5")} title={p.title} style={dims(p)} />;
}

export function DocumentTextIcon(p: IconProps) {
  return <HiOutlineDocumentText className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function CSVIcon(p: IconProps) {
  return (
    <BsFiletypeCsv className={cls(p, "file-type csv")} size={32} title={p.title} style={dims(p)} />
  );
}

export function CustomerIcon(p: IconProps) {
  return <MdOutlinePersonOutline className={cls(p, "icon")} title={p.title} style={dims(p)} />;
}

export function EditIcon(p: IconProps) {
  return (
    <FiEdit className={cls(p, "feather feather-edit")} size={24} title={p.title} style={dims(p)} />
  );
}

export function EllipsisVerticalIcon(p: IconProps) {
  return <HiEllipsisVertical className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function EnvelopeIcon(p: IconProps) {
  return <HiOutlineEnvelope className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function EmailIcon(p: IconProps) {
  return <MdAlternateEmail className={cls(p, "icon")} title={p.title} style={dims(p)} />;
}

export function EyeIcon(p: IconProps) {
  return <HiOutlineEye className={cls(p, "w-6 h-6")} title={p.title} style={dims(p)} />;
}

export function EyeSlashIcon(p: IconProps) {
  return <HiOutlineEyeSlash className={cls(p, "w-6 h-6")} title={p.title} style={dims(p)} />;
}

export function FileCopyIcon(p: IconProps) {
  return <MdOutlineFileCopy className={cls(p, "dupe-icon")} title={p.title} style={dims(p)} />;
}

export function FileImageIcon(p: IconProps) {
  return <PiFileImageDuotone className={cls(p, "w-6 h-6")} title={p.title} style={dims(p)} />;
}

export function ControlsIcon(p: IconProps) {
  return <HiAdjustmentsHorizontal className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function FilterIcon(p: IconProps) {
  // Legacy rendered the Heroicons v1 *outline* path with fill=currentColor and no stroke.
  return (
    <HiOutlineFilter
      className={cls(p, "h-5 w-5")}
      fill="currentColor"
      stroke="none"
      title={p.title}
      style={dims(p)}
    />
  );
}

export function GitIcon(p: IconProps) {
  return <FaGithub className={cls(p, "h-6 w-6")} title={p.title} style={dims(p)} />;
}

export function HashtagIcon(p: IconProps) {
  return <HiOutlineHashtag className={cls(p, "w-6 h-6")} title={p.title} style={dims(p)} />;
}

export function HeartIcon(p: IconProps & { filled?: boolean }) {
  const Glyph = p.filled ? HiHeart : HiOutlineHeart;
  return <Glyph className={cls(p, "h-6 w-6 flex-shrink-0")} title={p.title} style={dims(p)} />;
}

export function HomeIcon(p: IconProps) {
  return <HiHome className={cls(p, "flex-shrink-0 h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function HomeOutlineIcon(p: IconProps) {
  return <MdOutlineHome className={cls(p, "mail-count-icon")} title={p.title} style={dims(p)} />;
}

export function HomeWorkIcon(p: IconProps) {
  return (
    <MdOutlineHomeWork className={cls(p, "mail-count-icon")} title={p.title} style={dims(p)} />
  );
}

export function JSONIcon(p: IconProps) {
  return (
    <BsFiletypeJson
      className={cls(p, "file-type json")}
      size={32}
      title={p.title}
      style={dims(p)}
    />
  );
}

export function LeftArrowIcon(p: IconProps) {
  return <MdArrowBack className={cls(p)} title={p.title} style={dims(p)} />;
}

export function LockClosedIcon(p: IconProps) {
  return <HiOutlineLockClosed className={cls(p, "w-5 h-5")} title={p.title} style={dims(p)} />;
}

export function LogoutIcon(p: IconProps) {
  return <MdLogout className={cls(p)} size={24} title={p.title} style={dims(p)} />;
}

export function MailboxFlagIcon(p: IconProps) {
  return <BsMailboxFlag className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function MagnifyingGlassIcon(p: IconProps) {
  return <MdSearch className={cls(p)} title={p.title} style={dims(p)} />;
}

export function MapPinIcon(p: IconProps) {
  return <HiOutlineMapPin className={cls(p, "w-6 h-6")} title={p.title} style={dims(p)} />;
}

export function MyLocationIcon(p: IconProps) {
  return <MdMyLocation className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function MinusIcon(p: IconProps) {
  return <HiMinusSm className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function PDFIcon(p: IconProps) {
  return (
    <BsFiletypePdf className={cls(p, "file-type pdf")} size={32} title={p.title} style={dims(p)} />
  );
}

export function PencilIcon(p: IconProps) {
  return <HiOutlinePencil className={cls(p, "w-6 h-6")} title={p.title} style={dims(p)} />;
}

export function PencilSquareIcon(p: IconProps) {
  return <HiOutlinePencilSquare className={cls(p, "w-6 h-6")} title={p.title} style={dims(p)} />;
}

export function PlusIcon(p: IconProps) {
  return <HiPlusSm className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function RenameIcon(p: IconProps) {
  return <VscRename className={cls(p, "h-5 w-5")} title={p.title} style={dims(p)} />;
}

export function RestoreIcon(p: IconProps) {
  return <MdRestore className={cls(p, "icons restore-icon")} title={p.title} style={dims(p)} />;
}

export function RightArrowIcon(p: IconProps) {
  return <MdArrowForward className={cls(p)} title={p.title} style={dims(p)} />;
}

export function SaveDiskIcon(p: IconProps) {
  return <MdOutlineSave className={cls(p, "menu-icon save")} title={p.title} style={dims(p)} />;
}

export function ShieldCheckIcon(p: IconProps) {
  return <HiOutlineShieldCheck className={cls(p, "w-6 h-6")} title={p.title} style={dims(p)} />;
}

export function ShoppingBagIcon(p: IconProps) {
  return (
    <HiOutlineShoppingBag
      className={cls(p, "h-6 w-6")}
      title={p.title}
      style={{ margin: "auto", ...dims(p) }}
    />
  );
}

export function SmartphoneIcon(p: IconProps) {
  return <MdSmartphone className={cls(p, "icon")} title={p.title} style={dims(p)} />;
}

// Legacy StarIcon took `{ rating, review }` and picked text-yellow-400 / text-gray-200 itself.
// That colour choice now belongs to the caller via className.
export function StarIcon(p: IconProps) {
  return (
    <HiOutlineStar
      className={cls(p, "h-5 w-5 flex-shrink-0")}
      fill="currentColor"
      title={p.title}
      style={dims(p)}
    />
  );
}

export function TickIcon(p: IconProps) {
  return <MdCheck className={cls(p)} title={p.title} style={dims(p)} />;
}

export function UserCircleIcon(p: IconProps) {
  return <HiOutlineUserCircle className={cls(p, "w-6 h-6")} title={p.title} style={dims(p)} />;
}

export function UserIcon(p: IconProps) {
  return <HiUser className={cls(p, "w-7 h-7 mt-1")} title={p.title} style={dims(p)} />;
}

export function StopIcon(p: IconProps) {
  return (
    <HiOutlineStop className={cls(p, "h-6 w-6 text-red-600")} title={p.title} style={dims(p)} />
  );
}

export function XCircleIcon(p: IconProps) {
  return (
    <HiOutlineXCircle className={cls(p, "h-5 w-5 text-red-400")} title={p.title} style={dims(p)} />
  );
}

export function XMLIcon(p: IconProps) {
  return (
    <BsFiletypeXml className={cls(p, "file-type xml")} size={32} title={p.title} style={dims(p)} />
  );
}

export function XMarkIcon(p: IconProps) {
  return <HiOutlineXMark className={cls(p, "w-6 h-6")} title={p.title} style={dims(p)} />;
}

// ---------------------------------------------------------------------------
// Bespoke artwork, ported verbatim
// ---------------------------------------------------------------------------

export function AddressedMailIcon(p: IconProps) {
  return (
    <svg
      version="1.1"
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x="0px"
      y="0px"
      width={p.width}
      height={p.height}
      viewBox="0 0 200 200"
      enableBackground="new 0 0 200 200"
      xmlSpace="preserve"
      aria-hidden={!p.title}
      className={cls(p, "addressed-mail-icon")}
    >
      <title>{p.title}</title>
      <g>
        <rect y="0" className="st0" width="200" height="200" />
      </g>
      <g>
        <path
          className="st1"
          d="M121.6,9.8h6.2c0.6,0,1.1,0.5,1.1,1.1l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8l0,0
                c0.8,0,1.5-0.3,1.9-0.8c0.5-0.5,0.8-1.1,0.8-1.9l0,0c0-0.6,0.5-1.1,1.1-1.1h7.8c0.6,0,1.1,0.5,1.1,1.1l0,0c0,0.8,0.3,1.5,0.8,1.9
                c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.8,0,1.5-0.3,1.9-0.8c0.5-0.5,0.8-1.1,0.8-1.9l0,0c0-0.6,0.5-1.1,1.1-1.1h7.8c0.6,0,1.1,0.5,1.1,1.1
                l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.8,0,1.5-0.3,1.9-0.8c0.5-0.5,0.8-1.1,0.8-1.9l0,0c0-0.6,0.5-1.1,1.1-1.1
                h7.8c0.6,0,1.1,0.5,1.1,1.1l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.8,0,1.5-0.3,1.9-0.8c0.5-0.5,0.8-1.1,0.8-1.9
                l0,0c0-0.6,0.5-1.1,1.1-1.1h6.2c0.6,0,1.1,0.5,1.1,1.1v6.6c0,0.6-0.5,1.1-1.1,1.1l0,0c-0.8,0-1.5,0.3-1.9,0.8
                c-0.5,0.5-0.8,1.1-0.8,1.9l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.6,0,1.1,0.5,1.1,1.1v7.5
                c0,0.6-0.5,1.1-1.1,1.1l0,0c-0.8,0-1.5,0.3-1.9,0.8c-0.5,0.5-0.8,1.1-0.8,1.9l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8
                l0,0c0.6,0,1.1,0.5,1.1,1.1v7.8c0,0.6-0.5,1.1-1.1,1.1l0,0c-0.8,0-1.5,0.3-1.9,0.8c-0.5,0.5-0.8,1.1-0.8,1.9l0,0
                c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.6,0,1.1,0.5,1.1,1.1v7.8c0,0.6-0.5,1.1-1.1,1.1l0,0c-0.8,0-1.5,0.3-1.9,0.8
                c-0.5,0.5-0.8,1.1-0.8,1.9l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.6,0,1.1,0.5,1.1,1.1v7.8
                c0,0.6-0.5,1.1-1.1,1.1l0,0c-0.8,0-1.5,0.3-1.9,0.8c-0.5,0.5-0.8,1.1-0.8,1.9l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8
                l0,0c0.6,0,1.1,0.5,1.1,1.1v6.6c0,0.6-0.5,1.1-1.1,1.1h-6.2c-0.6,0-1.1-0.5-1.1-1.1l0,0c0-0.8-0.3-1.5-0.8-1.9
                c-0.5-0.5-1.1-0.8-1.9-0.8l0,0c-0.8,0-1.5,0.3-1.9,0.8s-0.8,1.1-0.8,1.9l0,0c0,0.6-0.5,1.1-1.1,1.1h-7.8c-0.6,0-1.1-0.5-1.1-1.1
                l0,0c0-0.8-0.3-1.5-0.8-1.9c-0.5-0.5-1.1-0.8-1.9-0.8l0,0c-0.8,0-1.5,0.3-1.9,0.8c-0.5,0.5-0.8,1.1-0.8,1.9l0,0
                c0,0.6-0.5,1.1-1.1,1.1h-7.8c-0.6,0-1.1-0.5-1.1-1.1l0,0c0-0.8-0.3-1.5-0.8-1.9s-1.1-0.8-1.9-0.8l0,0c-0.8,0-1.5,0.3-1.9,0.8
                c-0.5,0.5-0.8,1.1-0.8,1.9l0,0c0,0.6-0.5,1.1-1.1,1.1h-7.8c-0.6,0-1.1-0.5-1.1-1.1l0,0c0-0.8-0.3-1.5-0.8-1.9
                c-0.5-0.5-1.1-0.8-1.9-0.8l0,0c-0.8,0-1.5,0.3-1.9,0.8c-0.5,0.5-0.8,1.1-0.8,1.9l0,0c0,0.6-0.5,1.1-1.1,1.1h-6.2
                c-0.6,0-1.1-0.5-1.1-1.1v-6.6c0-0.6,0.5-1.1,1.1-1.1l0,0c0.8,0,1.5-0.3,1.9-0.8s0.8-1.1,0.8-1.9l0,0c0-0.8-0.3-1.5-0.8-1.9
                s-1.1-0.8-1.9-0.8l0,0c-0.6,0-1.1-0.5-1.1-1.1v-7.8c0-0.6,0.5-1.1,1.1-1.1l0,0c0.8,0,1.5-0.3,1.9-0.8s0.8-1.1,0.8-1.9l0,0
                c0-0.8-0.3-1.5-0.8-1.9s-1.1-0.8-1.9-0.8l0,0c-0.6,0-1.1-0.5-1.1-1.1v-7.8c0-0.6,0.5-1.1,1.1-1.1l0,0c0.8,0,1.5-0.3,1.9-0.8
                s0.8-1.1,0.8-1.9l0,0c0-0.8-0.3-1.5-0.8-1.9c-0.5-0.5-1.1-0.8-1.9-0.8l0,0c-0.6,0-1.1-0.5-1.1-1.1v-7.8c0-0.6,0.5-1.1,1.1-1.1l0,0
                c0.8,0,1.5-0.3,1.9-0.8s0.8-1.1,0.8-1.9l0,0c0-0.8-0.3-1.5-0.8-1.9s-1.1-0.8-1.9-0.8l0,0c-0.6,0-1.1-0.5-1.1-1.1V25
                c0-0.6,0.5-1.1,1.1-1.1l0,0c0.8,0,1.5-0.3,1.9-0.8c0.5-0.5,0.8-1.1,0.8-1.9l0,0c0-0.8-0.3-1.5-0.8-1.9c-0.5-0.5-1.1-0.8-1.9-0.8
                l0,0c-0.6,0-1.1-0.5-1.1-1.1v-6.6C120.4,10.3,121.1,9.8,121.6,9.8z"
        />
      </g>
      <g>
        <path
          d="M121.6,9.8h6.2c0.6,0,1.1,0.5,1.1,1.1l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.8,0,1.5-0.3,1.9-0.8
                c0.5-0.5,0.8-1.1,0.8-1.9l0,0c0-0.6,0.5-1.1,1.1-1.1h7.8c0.6,0,1.1,0.5,1.1,1.1l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8
                l0,0c0.8,0,1.5-0.3,1.9-0.8c0.5-0.5,0.8-1.1,0.8-1.9l0,0c0-0.6,0.5-1.1,1.1-1.1h7.8c0.6,0,1.1,0.5,1.1,1.1l0,0
                c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.8,0,1.5-0.3,1.9-0.8c0.5-0.5,0.8-1.1,0.8-1.9l0,0c0-0.6,0.5-1.1,1.1-1.1h7.8
                c0.6,0,1.1,0.5,1.1,1.1l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.8,0,1.5-0.3,1.9-0.8c0.5-0.5,0.8-1.1,0.8-1.9l0,0
                c0-0.6,0.5-1.1,1.1-1.1h6.2c0.6,0,1.1,0.5,1.1,1.1v6.6c0,0.6-0.5,1.1-1.1,1.1l0,0c-0.8,0-1.5,0.3-1.9,0.8c-0.5,0.5-0.8,1.1-0.8,1.9
                l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.6,0,1.1,0.5,1.1,1.1v7.5c0,0.6-0.5,1.1-1.1,1.1l0,0
                c-0.8,0-1.5,0.3-1.9,0.8c-0.5,0.5-0.8,1.1-0.8,1.9l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.6,0,1.1,0.5,1.1,1.1
                v7.8c0,0.6-0.5,1.1-1.1,1.1l0,0c-0.8,0-1.5,0.3-1.9,0.8c-0.5,0.5-0.8,1.1-0.8,1.9l0,0c0,0.8,0.3,1.5,0.8,1.9
                c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.6,0,1.1,0.5,1.1,1.1v7.8c0,0.6-0.5,1.1-1.1,1.1l0,0c-0.8,0-1.5,0.3-1.9,0.8
                c-0.5,0.5-0.8,1.1-0.8,1.9l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8l0,0c0.6,0,1.1,0.5,1.1,1.1v7.8
                c0,0.6-0.5,1.1-1.1,1.1l0,0c-0.8,0-1.5,0.3-1.9,0.8c-0.5,0.5-0.8,1.1-0.8,1.9l0,0c0,0.8,0.3,1.5,0.8,1.9c0.5,0.5,1.1,0.8,1.9,0.8
                l0,0c0.6,0,1.1,0.5,1.1,1.1v6.6c0,0.6-0.5,1.1-1.1,1.1h-6.2c-0.6,0-1.1-0.5-1.1-1.1l0,0c0-0.8-0.3-1.5-0.8-1.9
                c-0.5-0.5-1.1-0.8-1.9-0.8l0,0c-0.8,0-1.5,0.3-1.9,0.8s-0.8,1.1-0.8,1.9l0,0c0,0.6-0.5,1.1-1.1,1.1h-7.8c-0.6,0-1.1-0.5-1.1-1.1
                l0,0c0-0.8-0.3-1.5-0.8-1.9c-0.5-0.5-1.1-0.8-1.9-0.8l0,0c-0.8,0-1.5,0.3-1.9,0.8c-0.5,0.5-0.8,1.1-0.8,1.9l0,0
                c0,0.6-0.5,1.1-1.1,1.1h-7.8c-0.6,0-1.1-0.5-1.1-1.1l0,0c0-0.8-0.3-1.5-0.8-1.9s-1.1-0.8-1.9-0.8l0,0c-0.8,0-1.5,0.3-1.9,0.8
                c-0.5,0.5-0.8,1.1-0.8,1.9l0,0c0,0.6-0.5,1.1-1.1,1.1h-7.8c-0.6,0-1.1-0.5-1.1-1.1l0,0c0-0.8-0.3-1.5-0.8-1.9
                c-0.5-0.5-1.1-0.8-1.9-0.8l0,0c-0.8,0-1.5,0.3-1.9,0.8c-0.5,0.5-0.8,1.1-0.8,1.9l0,0c0,0.6-0.5,1.1-1.1,1.1h-6.2
                c-0.6,0-1.1-0.5-1.1-1.1v-6.6c0-0.6,0.5-1.1,1.1-1.1l0,0c0.8,0,1.5-0.3,1.9-0.8s0.8-1.1,0.8-1.9l0,0c0-0.8-0.3-1.5-0.8-1.9
                s-1.1-0.8-1.9-0.8l0,0c-0.6,0-1.1-0.5-1.1-1.1v-7.8c0-0.6,0.5-1.1,1.1-1.1l0,0c0.8,0,1.5-0.3,1.9-0.8s0.8-1.1,0.8-1.9l0,0
                c0-0.8-0.3-1.5-0.8-1.9s-1.1-0.8-1.9-0.8l0,0c-0.6,0-1.1-0.5-1.1-1.1v-7.8c0-0.6,0.5-1.1,1.1-1.1l0,0c0.8,0,1.5-0.3,1.9-0.8
                s0.8-1.1,0.8-1.9l0,0c0-0.8-0.3-1.5-0.8-1.9c-0.5-0.5-1.1-0.8-1.9-0.8l0,0c-0.6,0-1.1-0.5-1.1-1.1v-7.8c0-0.6,0.5-1.1,1.1-1.1l0,0
                c0.8,0,1.5-0.3,1.9-0.8s0.8-1.1,0.8-1.9l0,0c0-0.8-0.3-1.5-0.8-1.9s-1.1-0.8-1.9-0.8l0,0c-0.6,0-1.1-0.5-1.1-1.1V25
                c0-0.6,0.5-1.1,1.1-1.1l0,0c0.8,0,1.5-0.3,1.9-0.8c0.5-0.5,0.8-1.1,0.8-1.9l0,0c0-0.8-0.3-1.5-0.8-1.9c-0.5-0.5-1.1-0.8-1.9-0.8
                l0,0c-0.6,0-1.1-0.5-1.1-1.1v-6.6C120.4,10.3,121.1,9.8,121.6,9.8z M126.8,11.9h-4.1v4.5c1,0.2,1.8,0.6,2.4,1.3
                c1,1,1.5,2.1,1.5,3.6l0,0c0,1.3-0.5,2.6-1.5,3.6c-0.6,0.6-1.5,1.1-2.4,1.3V32c1,0.2,1.8,0.6,2.4,1.3c1,1,1.5,2.1,1.5,3.6l0,0
                c0,1.3-0.5,2.6-1.5,3.6c-0.6,0.6-1.5,1.1-2.4,1.3v5.7c1,0.2,1.8,0.6,2.4,1.3c1,1,1.5,2.1,1.5,3.6l0,0c0,1.3-0.5,2.6-1.5,3.6
                c-0.6,0.6-1.5,1.1-2.4,1.3V63c1,0.2,1.8,0.6,2.4,1.3c1,1,1.5,2.1,1.5,3.6l0,0c0,1.3-0.5,2.6-1.5,3.6c-0.6,0.6-1.5,1.1-2.4,1.3v5.5
                c1,0.2,1.8,0.6,2.4,1.3c1,1,1.5,2.1,1.5,3.6l0,0c0,1.3-0.5,2.6-1.5,3.6c-0.6,0.6-1.5,1.1-2.4,1.3v4.5h4.1c0.2-1,0.6-1.8,1.3-2.4
                c1-1,2.1-1.5,3.6-1.5l0,0c1.3,0,2.6,0.5,3.6,1.5c0.6,0.6,1.1,1.5,1.3,2.4h5.8c0.2-1,0.6-1.8,1.3-2.4c1-1,2.1-1.5,3.6-1.5l0,0
                c1.3,0,2.6,0.5,3.6,1.5c0.6,0.6,1.1,1.5,1.3,2.4h5.8c0.2-1,0.6-1.8,1.3-2.4c1-1,2.1-1.5,3.6-1.5l0,0c1.3,0,2.6,0.5,3.6,1.5
                c0.6,0.6,1.1,1.5,1.3,2.4h5.8c0.2-1,0.6-1.8,1.3-2.4c1-1,2.1-1.5,3.6-1.5l0,0c1.3,0,2.6,0.5,3.6,1.5c0.6,0.6,1.1,1.5,1.3,2.4h4.1
                v-4.5c-1-0.2-1.8-0.6-2.4-1.3c-1-1-1.5-2.1-1.5-3.6l0,0c0-1.3,0.5-2.6,1.5-3.6c0.6-0.6,1.5-1.1,2.4-1.3v-5.8
                c-1-0.2-1.8-0.6-2.4-1.3c-1-1-1.5-2.1-1.5-3.6l0,0c0-1.3,0.5-2.6,1.5-3.6c0.6-0.6,1.5-1.1,2.4-1.3v-5.5c-1-0.2-1.8-0.6-2.4-1.3
                c-1-1-1.5-2.1-1.5-3.6l0,0c0-1.3,0.5-2.6,1.5-3.6c0.6-0.6,1.5-1.1,2.4-1.3v-5.8c-1-0.2-1.8-0.6-2.4-1.3c-1-1-1.5-2.1-1.5-3.6l0,0
                c0-1.3,0.5-2.6,1.5-3.6c0.6-0.6,1.5-1.1,2.4-1.3V26c-1-0.2-1.8-0.6-2.4-1.3c-1-1-1.5-2.1-1.5-3.6l0,0c0-1.3,0.5-2.6,1.5-3.6
                c0.6-0.6,1.5-1.1,2.4-1.3v-4.5h-4.1c-0.2,1-0.6,1.8-1.3,2.4c-1,1-2.1,1.5-3.6,1.5l0,0c-1.3,0-2.6-0.5-3.6-1.5
                c-0.6-0.6-1.1-1.5-1.3-2.4h-5.7c-0.2,1-0.6,1.8-1.3,2.4c-1,1-2.1,1.5-3.6,1.5l0,0c-1.3,0-2.6-0.5-3.6-1.5c-0.6-0.6-1.1-1.5-1.3-2.4
                h-5.7c-0.2,1-0.6,1.8-1.3,2.4c-1,1-2.1,1.5-3.6,1.5l0,0c-1.3,0-2.6-0.5-3.6-1.5c-0.6-0.6-1.1-1.5-1.3-2.4H137
                c-0.2,1-0.6,1.8-1.3,2.4c-1,1-2.1,1.5-3.6,1.5l0,0c-1.3,0-2.6-0.5-3.6-1.5C127.4,13.7,126.9,12.9,126.8,11.9z"
        />
        <path
          d="M131.5,19.4H178c0.6,0,1.1,0.5,1.1,1.1v63.2c0,0.6-0.5,1.1-1.1,1.1h-46.5c-0.6,0-1.1-0.5-1.1-1.1V20.5
                C130.3,19.8,130.8,19.4,131.5,19.4z M176.9,21.6h-44.3v60.9h44.3C176.9,82.6,176.9,21.6,176.9,21.6z"
        />
      </g>
      <path
        d="M17.3,146.9h105.4c1.8,0,3.3-1.4,3.3-3.3c0-1.8-1.4-3.3-3.3-3.3H17.3c-1.8,0-3.3,1.4-3.3,3.3
            C14,145.5,15.4,146.9,17.3,146.9z"
      />
      <path
        d="M17.3,128.7h49.4c1.8,0,3.3-1.4,3.3-3.3c0-1.8-1.4-3.3-3.3-3.3H17.3c-1.8,0-3.3,1.4-3.3,3.3C14,127.1,15.4,128.7,17.3,128.7
            z"
      />
      <path
        d="M17.3,165.1h126.4c1.8,0,3.3-1.4,3.3-3.3c0-1.8-1.4-3.3-3.3-3.3H17.3c-1.8,0-3.3,1.4-3.3,3.3
            C14,163.7,15.4,165.1,17.3,165.1z"
      />
      <path
        d="M17.3,183.5h118.5c1.8,0,3.3-1.4,3.3-3.3c0-1.8-1.4-3.3-3.3-3.3H17.3c-1.8,0-3.3,1.4-3.3,3.3C14,182,15.4,183.5,17.3,183.5z
            "
      />
    </svg>
  );
}

// Carbon "download" glyph; react-icons has no Carbon set, so kept verbatim.
export function DownloadIcon(p: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={p.width ?? 32}
      height={p.height ?? 32}
      viewBox="0 0 32 32"
      aria-hidden={!p.title}
      className={cls(p, "file-type download-icon")}
    >
      <title>{p.title}</title>
      <path d="M26 24v4H6v-4H4v4a2 2 0 0 0 2 2h20a2 2 0 0 0 2-2v-4zm0-10l-1.41-1.41L17 20.17V2h-2v18.17l-7.59-7.58L6 14l10 10l10-10z" />
    </svg>
  );
}

// Material two-tone "visibility"; kept verbatim for the .eyeball / .pupil path classes.
export function EyeballIcon(p: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={p.width ?? "1em"}
      height={p.height ?? "1em"}
      viewBox="0 0 24 24"
      aria-hidden={!p.title}
      className={cls(p, "icon eyeball-icon")}
    >
      <title>{p.title}</title>
      <path
        fill="currentColor"
        className="eyeball"
        d="M12 6.5A9.77 9.77 0 0 0 3.18 12c1.65 3.37 5.02 5.5 8.82 5.5s7.17-2.13 8.82-5.5A9.77 9.77 0 0 0 12 6.5zm0 10c-2.48 0-4.5-2.02-4.5-4.5S9.52 7.5 12 7.5s4.5 2.02 4.5 4.5s-2.02 4.5-4.5 4.5z"
        opacity=".3"
      />
      <path
        fill="currentColor"
        className="pupil"
        d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 13A9.77 9.77 0 0 1 3.18 12C4.83 8.63 8.21 6.5 12 6.5s7.17 2.13 8.82 5.5A9.77 9.77 0 0 1 12 17.5zm0-10c-2.48 0-4.5 2.02-4.5 4.5s2.02 4.5 4.5 4.5s4.5-2.02 4.5-4.5s-2.02-4.5-4.5-4.5zm0 7a2.5 2.5 0 0 1 0-5a2.5 2.5 0 0 1 0 5z"
      />
    </svg>
  );
}

export function MapIcon(p: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={p.width}
      height={p.height}
      viewBox="0 0 64 64"
      aria-hidden={!p.title}
      className={cls(p, "map-icon")}
    >
      <title>{p.title}</title>
      <path fill="#dfe9ef" d="M21.6 56.6L2 58.2L8.1 5l19-1.2z" />
      <path fill="#bbc3c7" d="m43.4 62l-21.8-5.4l5.5-52.8l13.8 6z" />
      <path fill="#dfe9ef" d="M62 55.2L43.4 62L40.9 9.8l15-7.8z" />
      <path fill="#5df6ff" d="M22.1 52.1L6.8 53.3l4.7-44.8l15.2-.9z" />
      <path fill="#00c0cf" d="m43.2 58l-21.1-5.9l4.6-44.5l14.4 6z" />
      <path fill="#5df6ff" d="M57.9 52.5L43.2 58l-2.1-44.4L53 7.9z" />
      <g opacity=".7">
        <path
          fill="#f361f5"
          d="M21.6 36c-.3.1-.6.3-.8.5l.1.1C19 40.1 22 41.3 22 41.3c-1.2 6.1 1.3 8.1 1.3 8.1c-.6-4.6 3-7.6 3-7.6c1-4.7-3.3-5.6-4.7-5.8"
        />
        <path
          fill="#edba53"
          d="M20.4 35.6c-1.8-1.2-1.8-3.1-1.8-3.1c3 0 6.3-4.4 6.3-4.4c-2.2-7-3.1-1.1-3.1-1.1c-3.2-3.4.9-5 .9-5c-2.5-1-3.6-3.8-3.6-3.8c-.1 1.8-.9 1.7-.9 1.7c-5.7-3.1-6.7 1.9-6.7 1.9c.9 2.7-1 3-1 3c1.9.3 2.9-2.1 2.9-2.1c3.9 1.8 1.8 8.4 1.8 8.4c1.5.8 1.9 3.6 1.9 3.6c1.1.3 2.1.9 2.9 1.4c.1-.3.2-.4.4-.5"
        />
        <path fill="#eef8fb" d="M26.4 16.1s-1.7-1.1-4.5.6c0 0-.7 6.3 4.5-.6" />
        <path
          fill="#5f5cff"
          d="M30.6 17.5c-2.2-3.6-5.8 1.3-5.8 1.3c1.3 1 1.4 1.9 1.2 2.7c-.9 3 .3 3.9.3 3.9c.7-2.5 3.1-2.9 3.1-2.9c-.3-2.3 1.2-5 1.2-5"
        />
        <path
          fill="#ff7033"
          d="M36.7 33.7c-.2.1-.4.1-.6 0c-.5-.5-.8-1.1-1.1-1.7c-.1-.1-.1-.2-.1-.3c-1.7-.6-5-1.2-5.5 3.4c0 0 .6 3.1 2.7 1.5c0 0 2.3.4 1.4 4.5c0 0 .4 3.2 2.4 2.9c0 0 .5-4.6 3.1-8.2c-.1.1-1.1 1.1-2.3-2.1"
        />
        <path
          fill="#6c732d"
          d="M53.3 18.1s-4.9 1.7-10.3 1.2c0 0 .5-1.6-1.4-1.8c0 0-2.5 3-1.4 4.7c0 0-.5 3-1.1-1.4c0 0-1 2.6-2.5 2c0 0-.4 2.6-1.3.9c0 0 .7.3.9-.5c0 0-2.1-2.1-3.3 1.1c0 0-1 .4-.6 1.8c0 0 .7-1.2 1.1.6c0 0 .4-.3.5-2.6c0 0 1-.5.2 1.4c0 0 2.2 0-1.2 1.9c0 0-.2 0-.3-.7c0 0-.5 1.7-1.6 2.3c0 0 .3.7-.5 1c0 0-.4 1.1.6 1.1c0 0 .9-2.5 2.6-.5c0 0 .3-.1-.2-1.1c0 0 .9.3 1.2 1.6c0 0 .5-.1.6-1.2c0 0 .7-1.5 1.8.3c0 0-1.5-.4-1.9 1c0 0 .7-.3 1.1.1c0 0 0 .7-.4.9c.2.3.4.7.7.9c.2.2.2.4.1.6c.8 1.3 2.2 2.9 3.3.3c0 0-1.4.7-1.8-1.3c0 0 3.5.4 4.4 3.9c0 0 1-.7 1.3-3c0 0 1.2.1 2.6 3c0 0 .6-.1.3-2c0 0 2.3-.9 1-4.6c0 0 1.9 2.5 1.3-.7c0 0 2.1-2.4-.4-4c0 0 .1-1.8 1.6-1.5c0 0 1.5-3.7.8.7c0 0 0 .9 1 1.8c0 0 .4-1.4-.3-3c.2-.2 1.7-2.9 1.5-5.2"
        />
        <path
          fill="#f361f5"
          d="M31.9 27.8c.4-.9-.9-1.8-.9-1.8c.2 2 .5 2.6.9 1.8m-1.6.1s.6-.1.5-.8c0 0-.5 0-.5.8m-.2-4.1s-1.6 0-1 .8c.5.7 1-.8 1-.8"
        />
        <path fill="#edff54" d="M38.2 42.5s.5-.9.5-2.4c0 0-1.6 1.7-.5 2.4" />
        <path fill="#eef8fb" d="M48.4 36.6s-1.1 1-.9 1.9c.2.9 1.2-.2.9-1.9" />
        <path fill="#edff54" d="M53 39.8c.5-.8-2.7-1.8-2.7-1.8c.6.8 2.2 2.5 2.7 1.8" />
        <path fill="#f361f5" d="M49.3 39.9c.8-1.2-2.3-.6-2.3-.6s2 1.1 2.3.6" />
        <path
          fill="#f33"
          d="M50.5 40.2s-.6 1.3-2.9 1.8c0 0 .5 1 0 3c0 0 2.3-2.7 3.6 1.2c0 0 1.1.6 1.9-3c0 0-1-1.3-1.3-3.1c.1 0-.2 2.3-1.3.1m-1-8.7s2.6-.9 1.5-3c.1-.1.3-.2.3-.2c-1.1-1.7-.7.7-.7.7c.1-.2.2-.3.2-.4c0 .8-.1 1.9-1.3 2.9m-26.9-8s1.9 2.7 2.1-.3c.1-.7-.2-.8-.5-.8c-.1-2-1.5-2.6-1.5-2.6c-.9.5-.8 1.2-.8 1.2c.8.1 1.3 1.1 1.6 1.9c-.1.3-.4.6-.9.6"
        />
        <path fill="#edff54" d="M31.3 15.9c.6-1.2-1.2-2.6-1.2-2.6c.2 2.9.6 3.8 1.2 2.6" />
        <path fill="#fff" d="M29.8 14.8s-.7.1-.7 1.2c0 0 .9-.1.7-1.2" />
      </g>
    </svg>
  );
}

export function PDFColorIcon(p: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={p.width}
      height={p.height}
      viewBox="0 0 32 32"
      aria-hidden={!p.title}
      className={cls(p, "file-type pdf")}
    >
      <title>{p.title}</title>
      <path fill="#909090" d="m24.1 2.072l5.564 5.8v22.056H8.879V30h20.856V7.945L24.1 2.072" />
      <path fill="#f4f4f4" d="M24.031 2H8.808v27.928h20.856V7.873L24.03 2" />
      <path fill="#7a7b7c" d="M8.655 3.5h-6.39v6.827h20.1V3.5H8.655" />
      <path fill="#dd2025" d="M22.472 10.211H2.395V3.379h20.077v6.832" />
      <path
        fill="#464648"
        d="M9.052 4.534H7.745v4.8h1.028V7.715L9 7.728a2.042 2.042 0 0 0 .647-.117a1.427 1.427 0 0 0 .493-.291a1.224 1.224 0 0 0 .335-.454a2.13 2.13 0 0 0 .105-.908a2.237 2.237 0 0 0-.114-.644a1.173 1.173 0 0 0-.687-.65a2.149 2.149 0 0 0-.409-.104a2.232 2.232 0 0 0-.319-.026m-.189 2.294h-.089v-1.48h.193a.57.57 0 0 1 .459.181a.92.92 0 0 1 .183.558c0 .246 0 .469-.222.626a.942.942 0 0 1-.524.114m3.671-2.306c-.111 0-.219.008-.295.011L12 4.538h-.78v4.8h.918a2.677 2.677 0 0 0 1.028-.175a1.71 1.71 0 0 0 .68-.491a1.939 1.939 0 0 0 .373-.749a3.728 3.728 0 0 0 .114-.949a4.416 4.416 0 0 0-.087-1.127a1.777 1.777 0 0 0-.4-.733a1.63 1.63 0 0 0-.535-.4a2.413 2.413 0 0 0-.549-.178a1.282 1.282 0 0 0-.228-.017m-.182 3.937h-.1V5.392h.013a1.062 1.062 0 0 1 .6.107a1.2 1.2 0 0 1 .324.4a1.3 1.3 0 0 1 .142.526c.009.22 0 .4 0 .549a2.926 2.926 0 0 1-.033.513a1.756 1.756 0 0 1-.169.5a1.13 1.13 0 0 1-.363.36a.673.673 0 0 1-.416.106m5.08-3.915H15v4.8h1.028V7.434h1.3v-.892h-1.3V5.43h1.4v-.892"
      />
      <path
        fill="#dd2025"
        d="M21.781 20.255s3.188-.578 3.188.511s-1.975.646-3.188-.511Zm-2.357.083a7.543 7.543 0 0 0-1.473.489l.4-.9c.4-.9.815-2.127.815-2.127a14.216 14.216 0 0 0 1.658 2.252a13.033 13.033 0 0 0-1.4.288Zm-1.262-6.5c0-.949.307-1.208.546-1.208s.508.115.517.939a10.787 10.787 0 0 1-.517 2.434a4.426 4.426 0 0 1-.547-2.162Zm-4.649 10.516c-.978-.585 2.051-2.386 2.6-2.444c-.003.001-1.576 3.056-2.6 2.444ZM25.9 20.895c-.01-.1-.1-1.207-2.07-1.16a14.228 14.228 0 0 0-2.453.173a12.542 12.542 0 0 1-2.012-2.655a11.76 11.76 0 0 0 .623-3.1c-.029-1.2-.316-1.888-1.236-1.878s-1.054.815-.933 2.013a9.309 9.309 0 0 0 .665 2.338s-.425 1.323-.987 2.639s-.946 2.006-.946 2.006a9.622 9.622 0 0 0-2.725 1.4c-.824.767-1.159 1.356-.725 1.945c.374.508 1.683.623 2.853-.91a22.549 22.549 0 0 0 1.7-2.492s1.784-.489 2.339-.623s1.226-.24 1.226-.24s1.629 1.639 3.2 1.581s1.495-.939 1.485-1.035"
      />
      <path fill="#909090" d="M23.954 2.077V7.95h5.633l-5.633-5.873Z" />
      <path fill="#f4f4f4" d="M24.031 2v5.873h5.633L24.031 2Z" />
      <path
        fill="#fff"
        d="M8.975 4.457H7.668v4.8H8.7V7.639l.228.013a2.042 2.042 0 0 0 .647-.117a1.428 1.428 0 0 0 .493-.291a1.224 1.224 0 0 0 .332-.454a2.13 2.13 0 0 0 .105-.908a2.237 2.237 0 0 0-.114-.644a1.173 1.173 0 0 0-.687-.65a2.149 2.149 0 0 0-.411-.105a2.232 2.232 0 0 0-.319-.026m-.189 2.294h-.089v-1.48h.194a.57.57 0 0 1 .459.181a.92.92 0 0 1 .183.558c0 .246 0 .469-.222.626a.942.942 0 0 1-.524.114m3.67-2.306c-.111 0-.219.008-.295.011l-.235.006h-.78v4.8h.918a2.677 2.677 0 0 0 1.028-.175a1.71 1.71 0 0 0 .68-.491a1.939 1.939 0 0 0 .373-.749a3.728 3.728 0 0 0 .114-.949a4.416 4.416 0 0 0-.087-1.127a1.777 1.777 0 0 0-.4-.733a1.63 1.63 0 0 0-.535-.4a2.413 2.413 0 0 0-.549-.178a1.282 1.282 0 0 0-.228-.017m-.182 3.937h-.1V5.315h.013a1.062 1.062 0 0 1 .6.107a1.2 1.2 0 0 1 .324.4a1.3 1.3 0 0 1 .142.526c.009.22 0 .4 0 .549a2.926 2.926 0 0 1-.033.513a1.756 1.756 0 0 1-.169.5a1.13 1.13 0 0 1-.363.36a.673.673 0 0 1-.416.106m5.077-3.915h-2.43v4.8h1.028V7.357h1.3v-.892h-1.3V5.353h1.4v-.892"
      />
    </svg>
  );
}

// Tailwind UI breadcrumb slash; not in any react-icons set.
export function SlashIcon(p: IconProps) {
  return (
    <svg
      className={cls(p, "flex-shrink-0 h-5 w-5 text-gray-300")}
      aria-hidden={!p.title}
      xmlns="http://www.w3.org/2000/svg"
      width={p.width}
      height={p.height}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <title>{p.title}</title>
      <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
    </svg>
  );
}

export function TrashcanIcon(p: IconProps) {
  return (
    <svg
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x="0px"
      y="0px"
      width={p.width}
      height={p.height}
      viewBox="0 0 39.9 46.8"
      aria-hidden={!p.title}
      className={cls(p, "trash-can-icon")}
    >
      <title>{p.title}</title>
      <g>
        <g>
          <path
            d="M37.8,13.1H2.1C1,13.1,0,12.2,0,11V7.5c0-1.2,1-2.1,2.1-2.1h35.6c1.2,0,2.1,1,2.1,2.1V11C39.9,12.2,38.9,13.1,37.8,13.1z
			 M2.1,7.4C2.1,7.4,2,7.4,2,7.5V11c0,0.1,0.1,0.1,0.1,0.1h35.6c0.1,0,0.1-0.1,0.1-0.1V7.5c0-0.1-0.1-0.1-0.1-0.1H2.1z"
          />
        </g>
        <g>
          <path
            d="M30.6,46.8H9.3C7,46.8,5,45,4.9,42.7L2.8,11.1h34.3L35,42.7C34.8,45,32.9,46.8,30.6,46.8z M4.9,13.1l2,29.4
			C7,43.8,8,44.8,9.3,44.8h21.3c1.3,0,2.3-1,2.4-2.2l2-29.4H4.9z"
          />
        </g>
        <g>
          <path d="M19.9,41.4c-0.6,0-1-0.4-1-1v-23c0-0.6,0.4-1,1-1s1,0.4,1,1v23C20.9,41,20.5,41.4,19.9,41.4z" />
        </g>
        <g>
          <path d="M12.9,40.2c-0.6,0-1-0.4-1-1V17.7c0-0.6,0.4-1,1-1s1,0.4,1,1v21.5C13.9,39.8,13.5,40.2,12.9,40.2z" />
        </g>
        <g>
          <path d="M26.9,40.2c-0.6,0-1-0.4-1-1V17.7c0-0.6,0.4-1,1-1s1,0.4,1,1v21.5C27.9,39.8,27.5,40.2,26.9,40.2z" />
        </g>
      </g>
      <path d="M25.7,6.6c-0.1-0.4-1-4.1-5.8-4.1c-5,0-5.7,3.9-5.8,4.1l-2.5-0.4C12.1,4,14.3,0,19.9,0c5.6,0,7.8,4,8.2,6.2L25.7,6.6z" />
    </svg>
  );
}

export function UnaddressedMailIcon(p: IconProps) {
  return (
    <svg
      version="1.1"
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x="0px"
      y="0px"
      width={p.width}
      height={p.height}
      viewBox="0 0 200 200"
      enableBackground="new 0 0 200 200"
      xmlSpace="preserve"
      aria-hidden={!p.title}
      className={cls(p, "unaddressed-mail-icon")}
    >
      <title>{p.title}</title>
      <g>
        <g>
          <rect x="2.5" y="2.5" className="st0" width="195" height="195" />
          <path className="st1" d="M195,5v190H5V5H195 M200,0H0v200h200V0L200,0z" />
        </g>
      </g>
      <path
        className="st2"
        d="M15.9,28.9h143.5c3.7,0,6.6-2.9,6.6-6.6s-2.9-6.6-6.6-6.6H15.9c-3.7,0-6.6,2.9-6.6,6.6S12.3,28.9,15.9,28.9z"
      />
      <path
        className="st2"
        d="M15.9,55.6h82c3.7,0,6.6-2.9,6.6-6.6c0-3.7-2.9-6.6-6.6-6.6h-82c-3.7,0-6.6,2.9-6.6,6.6S12.3,55.6,15.9,55.6z"
      />
      <path
        className="st3"
        d="M14.7,99.4h40.8c1.8,0,3.3-2,3.3-4.5s-1.5-4.5-3.3-4.5H14.7c-1.8,0-3.3,2-3.3,4.5
				C11.4,97.4,12.9,99.4,14.7,99.4z"
      />
      <g>
        <path className="st4" d="M74,188.4l-0.1-0.2C73.9,188.3,73.9,188.3,74,188.4z" />
        <path
          className="st4"
          d="M181.9,151.1c-0.1-0.2-0.4-0.5-0.6-0.6c-6.9-19-15.4-33.3-25.7-43c-6.4-5.9-12.5-9.3-18.2-10.4
					c-7.1-1.1-11,1-11.9,6.1c0,0.4-0.1,0.7-0.1,1.2v0.1c0,0.8,0.2,1.7,0.7,2.5c-0.5,0.1-1,0.6-1.1,1.2c-0.4,1.3-4.4,8.2-12.4,20.6
					l-28.6,41.7l-7.9,10.2l-0.1,0.1c-1.8,2.8-2.8,4.7-2.8,5.8c0,0.7,0.1,1.2,0.6,1.7c0.4,0.5,1,0.8,1.6,1c0.5,0.1,1,0.1,1.5,0
					c21.2-4,44.3-7,69.4-9.2c6.7-0.7,15.1-0.6,25.3,0.1c0.1,0,0.1,0,0.2,0c0.1,0.1,0.4,0.4,0.5,0.5c2.7,2.2,5.1,3.5,7.3,3.8
					c0.4,0,0.6,0,0.8-0.1c3.5-1.8,5.7-4.5,6.4-8C188.5,170,186.8,161.6,181.9,151.1z M115.3,130.2c1.3-2.2,2.7-4.1,3.8-5.9
					c0.7,1.3,1.3,3.3,1.5,5.9c0,3.6-0.7,6.7-2.4,9.3c-1.3,2.2-3.3,3.6-5.8,4.6c-2.4,0.6-4.6,0.7-6.7,0.2L115.3,130.2z M93.5,183.2
					c1-1.8,2.2-3.5,4-5c1.7-1.3,4.4-2.3,7.6-2.8c2.3-0.1,4.1,0.1,5.2,1c1,0.6,2.1,2.1,3.1,4C106.7,181.2,100,182.2,93.5,183.2z
					 M146.1,176.7c-10.1,0.8-19.7,1.8-29.2,3c-1.6-3-3.1-5-4.7-6.1c-1.8-1.2-4.2-1.8-7.3-1.6h-0.1c-3.9,0.6-6.9,1.8-9.1,3.4
					c-2.8,2.2-4.5,4.7-5.3,7.8v0.1c-4.7,0.7-9.3,1.6-13.8,2.4c0.4-0.7,1-2.1,2.2-3.8l7.9-9.9l17.2-25.2c2.9,1,6.1,1,9.3,0h0.1
					c3.3-1.1,5.7-3.1,7.4-5.9c1.9-3.1,2.9-6.8,2.9-11v-0.1c-0.2-3.9-1.2-6.7-2.8-8.5c4.2-6.8,6.5-11,7.1-12.6c9.3,7,15.3,12,17.8,15
					v0.1c3.4,3.6,6.3,7.5,8.8,11.8l0.1,0.1c4,5.1,8.6,18,13.9,38.9l0.1,0.2c0.2,0.6,0.5,1.2,0.8,1.7C160.2,176,152.4,176,146.1,176.7z
					 M183.9,175.4c-0.6,2.5-2.1,4.4-4.5,5.7c-1.5-0.4-3.3-1.3-5.2-3c-1-0.7-1.8-2.1-2.7-4c-5.3-21.1-10.2-34.4-14.3-39.9
					c-2.7-4.5-5.7-8.5-9.2-12.2v0.1c-2.7-3.3-8.7-8.4-18.3-15.5c-0.8-0.8-1.3-1.7-1.3-2.4c0-0.2,0-0.5,0.1-0.8c0.5-3.1,3.3-4.2,8.4-3.5
					c5.1,1,10.7,4.2,16.6,9.7c10.2,9.7,18.8,24.2,25.6,43.7c0.1,0.4,0.5,0.7,0.8,0.8C183.7,163.1,185.1,170.1,183.9,175.4z"
        />
        <path
          className="st4"
          d="M153.8,149.5v-0.1c-1.3-2.8-2.7-4.7-4-5.8c-3.1-2.9-7.3-4.1-12.5-3.6h-0.1c-4,0.8-6.5,2.1-7.9,3.9
					c-1.6,2.1-2.4,4.8-2.7,8.2v0.1c0.1,2.8,1,5.5,2.5,8c1.5,2.1,3.3,3.6,5.7,4.8c0.1,0.4,0.5,0.7,0.8,0.8c4,1.7,7.8,1.6,11.3-0.5
					c4.7-2.7,7.3-6.1,7.6-10.3C154.8,153.2,154.6,151.3,153.8,149.5z M151.4,154.8c-0.2,3.1-2.3,5.7-6.1,7.9c-2.2,1.3-4.6,1.6-7.1,0.8
					c-0.1-0.2-0.4-0.5-0.7-0.6c-2.5-1-4.5-2.5-5.8-4.5c-1.2-2.1-1.9-4.1-2.1-6.3c0.4-2.8,1-5,2.2-6.5c0.8-1.1,2.8-1.9,5.7-2.5
					c4.4-0.4,7.6,0.6,10.2,2.8c1,0.8,2.1,2.4,3.1,4.7C151.3,151.9,151.5,153.4,151.4,154.8z"
        />
        <path
          className="st4"
          d="M118,165.6c-1.1-0.7-1.7-1.6-1.9-2.5c-0.2-1.6,0-3,0.6-4.1c0.5-0.7,0.2-1.7-0.5-2.1c-0.7-0.5-1.7-0.2-2.1,0.5
					c-1.1,1.8-1.5,3.9-1.1,6.3h0.1c0.5,1.8,1.6,3.3,3.1,4.4c0.7,0.5,1.6,0.4,2.1-0.4C118.8,166.9,118.7,166.1,118,165.6z"
        />
        <path
          className="st4"
          d="M98.3,166.3l-0.1,0.1c-0.2,0.2-0.6,0.5-1.2,0.6c-0.8,0.2-1.2,1.1-1.1,1.8c0.4,0.8,1.2,1.3,2.1,1.1
					c1.1-0.4,1.9-0.8,2.5-1.5c0.6-0.6,0.5-1.6-0.1-2.2C99.8,165.7,98.8,165.8,98.3,166.3z"
        />
        <path
          className="st4"
          d="M132,134.2c0.8,0.2,1.7-0.2,1.8-1.1c0.1-0.5,0.6-1.1,1.3-1.7c0.2-0.1,0.8-0.4,1.7-0.5c0.8-0.1,1.3-1,1.2-1.8
					s-1-1.3-1.8-1.2c-1.3,0.2-2.4,0.6-2.9,1.1c-0.1,0.1-0.1,0.1-0.2,0.2c-1.2,1.1-1.9,2.1-2.3,3.1C130.8,133.1,131.3,134,132,134.2z"
        />
        <path
          className="st4"
          d="M127.3,122.8c0.1,0.8,0.8,1.5,1.7,1.3c0.8-0.1,1.5-0.8,1.3-1.7c-0.2-2.3-1-3.9-2.1-4.8
					c-0.6-0.5-1.6-0.5-2.2,0.2c-0.5,0.6-0.5,1.6,0.2,2.2C126.8,120.5,127.2,121.4,127.3,122.8z"
        />
        <path
          className="st4"
          d="M124.1,171.5c-0.6,0.4-1.2,0.7-1.9,1c-0.7,0.2-1.2,0.4-1.3,0.2c0,0,0,0,0.1,0c-0.8-0.2-1.7,0.1-1.9,1
					c-0.4,0.8,0.1,1.7,0.8,1.9c0.1,0,0.4,0.1,0.6,0.1c0.6,0.1,1.5-0.1,2.7-0.5c1-0.2,1.8-0.7,2.7-1.3c0.7-0.5,0.8-1.5,0.4-2.1
					C125.7,171.3,124.8,171.1,124.1,171.5z"
        />
        <path
          className="st4"
          d="M160.4,163.4c-0.8,0-1.5,0.7-1.5,1.6c0,0.6,0,1-0.1,1.2c-0.1,0.2-0.2,0.6-0.5,1c-1.1,1.2-2.1,1.9-3,2.2
					c-0.8,0.2-1.3,1.1-1.1,1.8c0.2,1.2,1.1,1.7,1.8,1.5c1.6-0.4,3-1.5,4.6-3.3v-0.1c0.5-0.7,0.8-1.3,1.1-2.1c0.2-0.6,0.2-1.5,0.2-2.3
					C161.9,164,161.2,163.4,160.4,163.4z"
        />
        <path
          className="st4"
          d="M141.1,170.9c-0.2,0.1-0.7,0.1-1.2,0.1s-1.1,0-1.8,0c-0.8-0.1-1.6,0.6-1.7,1.3c-0.1,0.8,0.6,1.6,1.3,1.7
					c0.8,0.1,1.6,0.1,2.2,0.1c0.8,0,1.5-0.1,1.9-0.2c0.8-0.2,1.2-1.1,1.1-1.9C142.8,171.1,142,170.7,141.1,170.9z"
        />
      </g>
      <g>
        <path
          className="dollar-sign"
          d="M71.9,103.2v-1.9c-1.4,0-2.7-0.4-3.4-0.9l0.6-2.4c0.9,0.5,2,0.9,3.4,0.9c1.2,0,2-0.5,2-1.3c0-0.8-0.7-1.3-2.2-1.8
					c-2.2-0.7-3.6-1.7-3.6-3.7c0-1.8,1.3-3.2,3.4-3.6v-1.9h2v1.8c1.4,0,2.3,0.3,3,0.7l-0.6,2.3c-0.5-0.2-1.5-0.7-2.9-0.7
					c-1.3,0-1.7,0.6-1.7,1.2c0,0.6,0.7,1.1,2.4,1.7c2.4,0.8,3.4,2,3.4,3.8c0,1.8-1.3,3.3-3.6,3.7v2.1H71.9z"
        />
      </g>
      <g>
        <path
          className="dollar-sign"
          d="M83.1,103.2v-1.9c-1.4,0-2.7-0.4-3.4-0.9l0.6-2.4c0.9,0.5,2,0.9,3.4,0.9c1.2,0,2-0.5,2-1.3c0-0.8-0.7-1.3-2.2-1.8
					c-2.2-0.7-3.6-1.7-3.6-3.7c0-1.8,1.3-3.2,3.4-3.6v-1.9h2v1.8c1.4,0,2.3,0.3,3,0.7l-0.6,2.3c-0.5-0.2-1.5-0.7-2.9-0.7
					c-1.3,0-1.7,0.6-1.7,1.2c0,0.6,0.7,1.1,2.4,1.7c2.4,0.8,3.4,2,3.4,3.8c0,1.8-1.3,3.3-3.6,3.7v2.1H83.1z"
        />
      </g>
      <g>
        <path
          className="dollar-sign"
          d="M94.5,103.2v-1.9c-1.4,0-2.7-0.4-3.4-0.9l0.6-2.4c0.9,0.5,2,0.9,3.4,0.9c1.2,0,2-0.5,2-1.3c0-0.8-0.7-1.3-2.2-1.8
					c-2.2-0.7-3.6-1.7-3.6-3.7c0-1.8,1.3-3.2,3.4-3.6v-1.9h2v1.8c1.4,0,2.3,0.3,3,0.7l-0.6,2.3c-0.5-0.2-1.5-0.7-2.9-0.7
					c-1.3,0-1.7,0.6-1.7,1.2c0,0.6,0.7,1.1,2.4,1.7c2.4,0.8,3.4,2,3.4,3.8c0,1.8-1.3,3.3-3.6,3.7v2.1H94.5z"
        />
      </g>
      <path
        className="st3"
        d="M14.7,122.8h40.8c1.8,0,3.3-2,3.3-4.5s-1.5-4.5-3.3-4.5H14.7c-1.8,0-3.3,2-3.3,4.5S12.9,122.8,14.7,122.8z"
      />
      <g>
        <path
          className="dollar-sign"
          d="M71.9,126.6v-1.9c-1.4,0-2.7-0.4-3.4-0.9l0.6-2.4c0.9,0.5,2,0.9,3.4,0.9c1.2,0,2-0.5,2-1.3c0-0.8-0.7-1.3-2.2-1.8
					c-2.2-0.7-3.6-1.7-3.6-3.7c0-1.8,1.3-3.2,3.4-3.6v-1.9h2v1.8c1.4,0,2.3,0.3,3,0.7l-0.6,2.3c-0.5-0.2-1.5-0.7-2.9-0.7
					c-1.3,0-1.7,0.6-1.7,1.2c0,0.6,0.7,1.1,2.4,1.7c2.4,0.8,3.4,2,3.4,3.8c0,1.8-1.3,3.3-3.6,3.7v2.1H71.9z"
        />
      </g>
      <g>
        <path
          className="dollar-sign"
          d="M83.1,126.6v-1.9c-1.4,0-2.7-0.4-3.4-0.9l0.6-2.4c0.9,0.5,2,0.9,3.4,0.9c1.2,0,2-0.5,2-1.3c0-0.8-0.7-1.3-2.2-1.8
					c-2.2-0.7-3.6-1.7-3.6-3.7c0-1.8,1.3-3.2,3.4-3.6v-1.9h2v1.8c1.4,0,2.3,0.3,3,0.7l-0.6,2.3c-0.5-0.2-1.5-0.7-2.9-0.7
					c-1.3,0-1.7,0.6-1.7,1.2c0,0.6,0.7,1.1,2.4,1.7c2.4,0.8,3.4,2,3.4,3.8c0,1.8-1.3,3.3-3.6,3.7v2.1H83.1z"
        />
      </g>
    </svg>
  );
}

export function UploadIcon(p: IconProps) {
  return (
    <svg
      version="1.1"
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      x="0px"
      y="0px"
      width={p.width}
      height={p.height}
      viewBox="0 0 32 32"
      enableBackground="new 0 0 32 32"
      xmlSpace="preserve"
      aria-hidden={!p.title}
      className={cls(p, "upload-icon")}
    >
      <title>{p.title}</title>
      <path
        className="st0"
        d="M5.3,2.8c0-0.9,0.7-1.5,1.5-1.5h14.3l5.5,5.5v22.3c0,0.9-0.7,1.5-1.5,1.5H6.9c-0.9,0-1.5-0.7-1.5-1.5L5.3,2.8z"
      />
      <g>
        <path className="st1" d="M19.6,19.1L16,15.5l-3.6,3.6" />
        <path className="st1" d="M16,15.5v10.2" />
      </g>
      <g>
        <path
          className="st2"
          d="M19.3,9.2c-0.3,0-0.5-0.1-0.7-0.3c-0.2-0.2-0.3-0.5-0.3-0.8l0.8-5.8c0-0.4,0.3-0.7,0.7-0.8
				c0.1,0,0.2-0.1,0.3-0.1c0.3,0,0.5,0.1,0.7,0.3l5.5,5.5c0.3,0.3,0.4,0.7,0.2,1.1c-0.1,0.4-0.5,0.6-0.9,0.6
				C25.7,8.8,19.4,9.2,19.3,9.2z"
        />
        <path
          className="st3"
          d="M20.1,2.3l5.5,5.5l-6.3,0.3L20.1,2.3 M20.1,0.3c-0.2,0-0.4,0-0.6,0.1c-0.7,0.2-1.2,0.9-1.3,1.6l-0.8,5.8
				c-0.1,0.6,0.1,1.2,0.5,1.6c0.4,0.4,0.9,0.6,1.5,0.6c0,0,0.1,0,0.1,0l6.3-0.3c0.8,0,1.5-0.5,1.8-1.3C27.7,7.8,27.6,7,27,6.4
				l-5.5-5.5C21.1,0.5,20.6,0.3,20.1,0.3L20.1,0.3z"
        />
      </g>
    </svg>
  );
}

// Bespoke office-building drawing (not Material work_outline).
export function WorkOutlineIcon(p: IconProps) {
  return (
    <svg
      version="1.1"
      id="Layer_1"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width={p.width}
      height={p.height}
      viewBox="0 0 24 24"
      aria-hidden={!p.title}
      className={cls(p, "mail-count-icon")}
    >
      <title>{p.title}</title>
      <rect x="12.5" y="7" width="2" height="2" />
      <rect x="12.5" y="11" width="2" height="2" />
      <rect x="12.5" y="15" width="2" height="2" />
      <rect x="9.3" y="7" width="2" height="2" />
      <rect x="9.3" y="11" width="2" height="2" />
      <rect x="9.3" y="15" width="2" height="2" />
      <path d="M11.5,3h-6v2v16h7h6v-2V3H11.5z M16.5,19h-4h-5V6.4V5h4h5v12.6V19z" />
    </svg>
  );
}

// Tabler-shaped file-type-zip, but the legacy SVG carries no stroke/fill attrs (styled by CSS),
// so it is kept verbatim rather than swapped for TbFileTypeZip.
export function ZipDownloadIcon(p: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={p.width ?? 32}
      height={p.height ?? 32}
      viewBox="0 0 24 24"
      aria-hidden={!p.title}
      className={cls(p, "file-type zip")}
    >
      <title>{p.title}</title>
      <g strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
        <path d="M14 3v4a1 1 0 0 0 1 1h4" />
        <path d="M5 12V5a2 2 0 0 1 2-2h7l5 5v4m-3 6h1.5a1.5 1.5 0 0 0 0-3H16v6m-4-6v6m-7-6h3l-3 6h3" />
      </g>
    </svg>
  );
}
